import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Plus, UserCheck } from "lucide-react";
import Modal from "@/components/Modal";
import { FollowOrganizerOrNotAPI } from "@/services/api/user/followOrganizer";

interface StudentFollowButtonProps {
  organizerId: string;
  following: boolean;
  onChange?: (following: boolean) => void;
}

/**
 * Follow / unfollow pill for a society organizer, backed by the
 * `followOrganizer` endpoints.
 */
const StudentFollowButton: React.FC<StudentFollowButtonProps> = ({
  organizerId,
  following,
  onChange,
}) => {
  const { t } = useTranslation();
  const [pending, setPending] = useState(false);
  const [errorVisible, setErrorVisible] = useState(false);

  const handleToggle = async () => {
    if (pending) return;
    setPending(true);
    const next = !following;
    const resp = await FollowOrganizerOrNotAPI(following, organizerId);
    setPending(false);
    if (resp?.status === 0) {
      onChange?.(next);
      return;
    }
    setErrorVisible(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleToggle}
        disabled={pending}
        className={`inline-flex shrink-0 items-center gap-1 rounded-full border-2 px-3 py-1 text-xs font-bold transition disabled:opacity-60 ${
          following
            ? "border-[#ffa217] bg-white text-[#ffa217]"
            : "border-gray-200 bg-white text-gray-500 hover:border-[#ffa217] hover:text-[#ffa217]"
        }`}
      >
        {pending ? (
          <Loader2 size={12} className="animate-spin" />
        ) : following ? (
          <UserCheck size={12} />
        ) : (
          <Plus size={12} />
        )}
        {following ? t("Following") : t("Follow")}
      </button>

      <Modal visible={errorVisible} onClose={() => setErrorVisible(false)}>
        <div className="rounded-2xl bg-white p-5 text-center shadow-lg">
          <div className="mb-1.5 text-base font-semibold text-gray-900">
            {t("Something went wrong")}
          </div>
          <p className="mb-4 text-sm text-gray-500">{t("Please try again later.")}</p>
          <button
            type="button"
            onClick={() => setErrorVisible(false)}
            className="w-full rounded-xl bg-[#ffa217] py-2.5 text-sm font-bold text-white"
          >
            {t("OK")}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default StudentFollowButton;
